import { sendWebSocketConnectionCheckStart, sendWebSocketConnectionCheckResult } from '../stat/factHelpers';
import { RoundRobinSelector } from '../utils/round-robin-selector';
import axios from 'axios';
import { container } from '../utils/container/container';
import { FeatureFlagsLoader } from '@scorewarrior/rubens_web_lib';
import { ServiceIds } from '../utils/container/service-ids';
import { FlagNames } from '../utils/feature-flags/flag-names';

const TEST_TICK = 10 * 1000; // 10 sec
const TEST_ATTEMPT_TIMEOUT = 10 * 1000; // 10 sec
const TEST_TIMEOUT = 2 * 60 * 1000; // 2 min

export enum ConnectionCheckType {
    HTTPS,
    WSS,
}

export async function checkWebSocketConnection() {
    const ffLoader = container.get<FeatureFlagsLoader>(ServiceIds.FEATURE_FLAG_LOADER);
    const connectionCheckEnabled = ffLoader.get(FlagNames.CONNECTION_CHECK_ENABLED).isOn();
    if (!connectionCheckEnabled) {
        return;
    }

    try {
        const loginTs = new Date().getTime();

        await sendWebSocketConnectionCheckStart(loginTs);

        const urls = window.app.config.client_vars.cluster.journal_v2_urls;

        await Promise.all([
            performConnectionTest(ConnectionCheckType.HTTPS, loginTs, new RoundRobinSelector(urls)),
            performConnectionTest(ConnectionCheckType.WSS, loginTs, new RoundRobinSelector(urls))
        ]);
    } catch (err) {
        window.Sentry.captureException(err);
        console.error('Failed to check journal connection', err);
    }
}

async function performConnectionTest(
    type: ConnectionCheckType,
    loginTs: number,
    urlSelector: RoundRobinSelector<string>
) {
    await new Promise<void>((resolve) => {
        const testStartTs = new Date().getTime();
        let attempt = 0;

        const testInterval = setInterval(async () => {
            attempt++;

            const url = urlSelector.getNext();

            let [result, attemptResultTs] = await (
                type == ConnectionCheckType.HTTPS
                    ? testHttpsConnection(url)
                    : testWssConnection(url)
            );

            await sendWebSocketConnectionCheckResult(
                type,
                result,
                attempt,
                isUsedFallbackUrl(url),
                attemptResultTs,
                loginTs
            );

            if (result) {
                clearInterval(testInterval);
                resolve();
                return;
            }

            const now = new Date().getTime();
            if (now - testStartTs > TEST_TIMEOUT) {
                clearInterval(testInterval);
                resolve();
                return;
            }
        }, TEST_TICK);
    });
}

async function testHttpsConnection(url: string): Promise<[boolean, number]> {
    const startTime = new Date().getTime();
    try {
        const axiosClearInstance = axios.create({ timeout: TEST_ATTEMPT_TIMEOUT });
        const response = await axiosClearInstance.get(concatUrl(url, 'https', '/https-test'));
        return [response.status === 200, (new Date().getTime()) - startTime];
    } catch {
        return [false, (new Date().getTime()) - startTime];
    }
}

async function testWssConnection(url: string): Promise<[boolean, number]> {
    return new Promise((resolve) => {
        const startTime = new Date().getTime();
        const ws = new WebSocket(concatUrl(url, 'wss', '/wss-test'));

        const timeout = setTimeout(() => {
            ws.close();
            resolve([false, new Date().getTime() - startTime]);
        }, TEST_ATTEMPT_TIMEOUT);

        ws.addEventListener('open', () => {
            clearTimeout(timeout);
            ws.close();
            resolve([true, (new Date().getTime()) - startTime]);
        });

        ws.addEventListener('error', () => {
            clearTimeout(timeout);
            resolve([false, (new Date().getTime()) - startTime]);
        });
    });
}

function concatUrl(url: string, protocol: string, endpoint: string): string {
    let urlWithoutProtocol = url.replace(/^.*?:\/\//, '') + endpoint;
    urlWithoutProtocol = urlWithoutProtocol.replace(/\/+/g, '/');
    return `${protocol}://${urlWithoutProtocol}`;
}

function isUsedFallbackUrl(url: string): boolean {
    const urls = window.app.config.client_vars.cluster.journal_v2_urls;
    if (urls.length < 2) return false;

    const extractDomain = (u: string) => u.replace(/^.*?:\/\//, '').split('/')[0];
    return extractDomain(url) !== extractDomain(urls[0]);
}
